import _ from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
import { SearchStatus, TravelTypes } from '../../constants';
import formatItinerariesFromCruiseOption from '../../helpers/formatItinerariesFromCruiseOption';

const initialState = {
  cruiseOptions: [],
  hotelOptions: [],
  flightOptions: [],
  carOptions: [],
  tourOptions: [],
  sendEmailCompareError: undefined,
  id: undefined,
  loggedInUserEmail: undefined,
  fromEmail: undefined,
  createdFor: undefined,
  emailRecipients: undefined,
  emailSubject: undefined,
  emailMessage: undefined,
  compareTravelOptionsHtml: undefined,
  compareTravelOptions: undefined,
  sendEmailCompareStatus: SearchStatus.INITIAL,
  getCompareStatus: SearchStatus.INITIAL,
  getCompareError: undefined,
};

const manageCompareItemsList = (items, itemToModify) => {
  // Remove paired flight segments to prevent orphaned outbound/inbound segments
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const keys = items[i].key.split('|');
    if (keys.length > 1 && keys.includes(itemToModify.key)) {
      items.splice(i, 1);
      return;
    }
  }
  const indexOfItineraryId = items && _.findIndex(items, (c) => c.key === itemToModify.key);
  if (indexOfItineraryId >= 0) {
    items.splice(indexOfItineraryId, 1);
  } else {
    items.push(itemToModify);
  }
  // Remove outbound segment when inbound segment is added to compare
  const flightSearchKeys = itemToModify.key.split('|');
  if (flightSearchKeys && flightSearchKeys.length > 1) {
    for (let i = items.length - 1; i >= 0; i -= 1) {
      if (flightSearchKeys.includes(items[i].key)) {
        items.splice(i, 1);
      }
    }
  }
};

const compareSlice = createSlice({
  name: 'compare',
  initialState,
  reducers: {
    addOrRemoveToCompare(
      state,
      { payload: { travelOptions, itineraryId, itineraryToRemove, searchObj } },
    ) {
      let matchedTravelOption =
        travelOptions && travelOptions.find((i) => i.key && i.key.indexOf(itineraryId) > -1);

      if (!travelOptions && itineraryToRemove) {
        switch (itineraryToRemove.mode) {
          case TravelTypes.hotel:
            matchedTravelOption =
              state.hotelOptions &&
              _.find(state.hotelOptions, (h) => h.itineraryId === itineraryId);
            break;
          case TravelTypes.carrental:
            matchedTravelOption =
              state.carOptions && _.find(state.carOptions, (h) => h.itineraryId === itineraryId);
            break;
          case TravelTypes.flight:
            matchedTravelOption =
              state.flightOptions &&
              _.find(state.flightOptions, (h) => h.itineraryId === itineraryId);
            break;
          case TravelTypes.tour:
            matchedTravelOption =
              state.tourOptions && _.find(state.tourOptions, (h) => h.itineraryId === itineraryId);
            break;
          default:
            break;
        }
      }
      if (!matchedTravelOption) return { ...state };

      const findCruiseOption =
        matchedTravelOption.mode === TravelTypes.cruise
          ? travelOptions &&
            formatItinerariesFromCruiseOption({ travelOptions }).find(
              (i) => i.itineraryId === itineraryId,
            )
          : {};
      const cruiseOption = matchedTravelOption.mode === TravelTypes.cruise && {
        ...findCruiseOption.travelOption,
        itineraryRows: _.filter(
          findCruiseOption.travelOption.itineraryRows,
          (r) => r.itineraryId === matchedTravelOption.itineraryId,
        ),
        key: itineraryId,
      };
      const hotelOption = matchedTravelOption.mode === TravelTypes.hotel && {
        ...matchedTravelOption,
        rateCodes: searchObj?.hotel?.rateCodes || [],
        doShowCommissionableRatesOnly: searchObj?.hotel?.doShowCommissionableRatesOnly,
        exactMatchRateCodesOnly: searchObj?.hotel?.exactMatchRateCodesOnly,
        showRefundableHotelRatesOnly: searchObj?.hotel?.showRefundableHotelRatesOnly,
      };
      const flightOption = matchedTravelOption.mode === TravelTypes.flight && matchedTravelOption;
      const carOption = matchedTravelOption.mode === TravelTypes.carrental && matchedTravelOption;
      const tourOption = matchedTravelOption.mode === TravelTypes.tour && matchedTravelOption;

      const cruiseOptions = [...state.cruiseOptions];
      const hotelOptions = [...state.hotelOptions];
      const flightOptions = [...state.flightOptions];
      const carOptions = [...state.carOptions];
      const tourOptions = [...state.tourOptions];

      if (cruiseOption) {
        manageCompareItemsList(cruiseOptions, cruiseOption);
      } else if (hotelOption) {
        manageCompareItemsList(hotelOptions, hotelOption);
      } else if (flightOption) {
        manageCompareItemsList(flightOptions, flightOption);
      } else if (carOption) {
        manageCompareItemsList(carOptions, carOption);
      } else if (tourOption) {
        manageCompareItemsList(tourOptions, tourOption);
      }

      return {
        ...state,
        cruiseOptions,
        hotelOptions,
        flightOptions,
        carOptions,
        tourOptions,
      };
    },
    removeAllCompareItems: () => ({
      ...initialState,
    }),
    setFields: (state, { payload }) => ({
      ...state,
      ...payload,
    }),
    sendEmailCompare: (state) => ({
      ...state,
      sendEmailCompareError: null,
      sendEmailCompareStatus: SearchStatus.SEARCHING,
    }),
    sendEmailCompareResponse: (state, { payload: { data = {}, error } }) => ({
      ...state,
      sendEmailCompareError: error,
      sendEmailCompareStatus: SearchStatus.DONE,
      ...data,
    }),
    getCompareRequest: (state) => ({
      ...state,
      getCompareStatus: SearchStatus.SEARCHING,
      getCompareError: undefined,
    }),
    getCompareResponse: (state, { payload: { data = {}, error } }) => ({
      ...state,
      getCompareStatus: SearchStatus.DONE,
      getCompareError: error,
      isViewingCompare: true,
      ...data,
    }),
    setIsViewingCompare: (state, { payload }) => ({
      ...state,
      isViewingCompare: payload,
    }),
  },
});

export const checkHaveCompareItems = (state) =>
  (state.compare.cruiseOptions && state.compare.cruiseOptions.length > 0) ||
  (state.compare.hotelOptions && state.compare.hotelOptions.length > 0) ||
  (state.compare.flightOptions && state.compare.flightOptions.length > 0) ||
  (state.compare.tourOptions && state.compare.tourOptions.length > 0) ||
  (state.compare.carOptions && state.compare.carOptions.length > 0);

export const {
  addOrRemoveToCompare,
  removeAllCompareItems,
  sendEmailCompare,
  sendEmailCompareResponse,
  setFields,
  getCompareRequest,
  getCompareResponse,
  setIsViewingCompare,
} = compareSlice.actions;
export default compareSlice.reducer;
